<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';

const DEFAULT_COLOR = '#64748B';

const store = useStore();
const { t } = useI18n();
const form = reactive({
  key: '',
  description: '',
  base_availability: 'busy',
  color: DEFAULT_COLOR,
});
const editingId = ref(null);
const statuses = computed(() => store.getters['accountStatuses/getStatuses']);
const customStatuses = computed(() =>
  statuses.value.filter(status => !status.builtin)
);
const limit = computed(() => store.getters['accountStatuses/getLimit']);
const canCreate = computed(() => store.getters['accountStatuses/canCreate']);

const reset = () => {
  Object.assign(form, {
    key: '',
    description: '',
    base_availability: 'busy',
    color: DEFAULT_COLOR,
  });
  editingId.value = null;
};
const edit = status => {
  Object.assign(form, status);
  editingId.value = status.id;
};
const save = async () => {
  try {
    const action = editingId.value ? 'update' : 'create';
    await store.dispatch(`accountStatuses/${action}`, {
      ...form,
      id: editingId.value,
    });
    reset();
    useAlert(t('ACCOUNT_STATUS_MGMT.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('ACCOUNT_STATUS_MGMT.ERROR'));
  }
};
const remove = async id => {
  await store.dispatch('accountStatuses/delete', id);
  useAlert(t('ACCOUNT_STATUS_MGMT.DELETED'));
};

onMounted(() => store.dispatch('accountStatuses/get'));
</script>

<template>
  <div class="flex flex-col gap-6 p-6 max-w-4xl">
    <div>
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ $t('ACCOUNT_STATUS_MGMT.TITLE') }}
      </h1>
      <p class="text-n-slate-11">{{ $t('ACCOUNT_STATUS_MGMT.DESCRIPTION') }}</p>
    </div>
    <p class="text-sm text-n-slate-10">
      {{
        $t('ACCOUNT_STATUS_MGMT.LIMIT_USAGE', {
          count: customStatuses.length,
          limit,
        })
      }}
    </p>
    <form
      v-if="editingId || canCreate"
      class="grid gap-3 p-4 border border-n-weak rounded-lg"
      @submit.prevent="save"
    >
      <input
        v-model="form.key"
        :disabled="editingId"
        required
        class="h-10 px-3 border border-n-weak rounded"
        :placeholder="$t('ACCOUNT_STATUS_MGMT.KEY')"
      />
      <input
        v-model="form.description"
        required
        class="h-10 px-3 border border-n-weak rounded"
        :placeholder="$t('ACCOUNT_STATUS_MGMT.STATUS_DESCRIPTION')"
      />
      <select
        v-model="form.base_availability"
        class="h-10 px-3 border border-n-weak rounded"
      >
        <option value="online">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE') }}
        </option>
        <option value="busy">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY') }}
        </option>
        <option value="offline">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE') }}
        </option>
      </select>
      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-11">
          {{ $t('ACCOUNT_STATUS_MGMT.COLOR') }}
        </label>
        <ColorPicker v-model="form.color" />
      </div>
      <div class="flex gap-2">
        <Button type="submit" :label="$t('ACCOUNT_STATUS_MGMT.SAVE')" /><Button
          v-if="editingId"
          type="button"
          color="slate"
          :label="$t('ACCOUNT_STATUS_MGMT.CANCEL')"
          @click="reset"
        />
      </div>
    </form>
    <p v-else class="p-4 rounded-lg bg-n-amber-2 text-n-amber-11">
      {{ $t('ACCOUNT_STATUS_MGMT.LIMIT_REACHED') }}
    </p>
    <div
      v-for="status in customStatuses"
      :key="status.id"
      class="flex items-center justify-between p-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3 min-w-0">
        <span
          class="size-3 rounded-sm flex-shrink-0"
          :style="{ backgroundColor: status.color }"
        />
        <div class="min-w-0">
          <p class="font-medium text-n-slate-12">{{ status.description }}</p>
          <p class="text-sm text-n-slate-10">
            {{
              $t('ACCOUNT_STATUS_MGMT.STATUS_DETAILS', {
                key: status.key,
                availability: status.base_availability,
              })
            }}
          </p>
        </div>
      </div>
      <div class="flex gap-2">
        <Button slate sm icon="i-woot-edit-pen" @click="edit(status)" /><Button
          slate
          sm
          icon="i-woot-bin"
          @click="remove(status.id)"
        />
      </div>
    </div>
  </div>
</template>
